"use client";
import { useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";

import {
  fetchEmergencyContacts,
  addEmergencyContact,
  deleteEmergencyContact,
  updateEmergencyContact, 
} from "../redux/thunks/emergencyContactThunks";

import useAuthSession from "../../authPatient/hooks/useAuthSession";

const cleanDigits = (phone) => String(phone || "").replace(/\D/g, "");

export default function useEmergencyContact() {
  const dispatch = useDispatch();
  const { effectiveUserId } = useAuthSession({ auto: true });

  // ✅ userId robuste
  const userId = effectiveUserId ? String(effectiveUserId) : null;

  // ✅ slice
  const st = useSelector((s) => s.emergencyContacts || {});
  const { byId = {}, allIds = [], isLoading = false, error = null, ui = {} } = st;

  // ✅ array
  const contacts = useMemo(
    () => (allIds || []).map((id) => byId?.[id]).filter(Boolean),
    [allIds, byId]
  );

  const status = isLoading ? "loading" : "idle";
  const isAddModalOpen = !!ui?.addModalOpen;

  /* =========================
   * FETCH once per userId
   * (si userId change, on refetch)
   * ========================= */
  const fetchedForRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    // si on a déjà fetch pour ce userId, ne refetch pas
    if (fetchedForRef.current === userId) return;

    fetchedForRef.current = userId;
    dispatch(fetchEmergencyContacts({ userId }));
  }, [userId, dispatch]);

  /* =========================
   * Refresh
   * ========================= */
  const handleRefresh = useCallback(() => {
    if (!userId) return;
    dispatch(fetchEmergencyContacts({ userId }));
  }, [userId, dispatch]);

  /* =========================
   * CREATE (garde solidité du code 2)
   * ========================= */
  const create = useCallback(
    async (values) => {
      if (!userId) {
        message.error("User non authentifié.");
        return { ok: false };
      }

      const payload = {
        name: String(values?.name || "").trim(),
        phone: String(values?.phone || "").trim(),
        relation: String(values?.relation || "").trim() || undefined,
      };

      if (!payload.name || !payload.phone) {
        message.error("Nom et téléphone requis.");
        return { ok: false };
      }

      const res = await dispatch(
        addEmergencyContact({ userId, contact: payload })
      );

      if (addEmergencyContact.fulfilled.match(res)) {
        message.success("Contact added");
        // ✅ resync
        await dispatch(fetchEmergencyContacts({ userId }));
        return { ok: true, data: res.payload };
      }

      const errPayload = res?.payload;
      const errMsg =
        errPayload?.message ||
        (typeof errPayload === "string" ? errPayload : null) ||
        res?.error?.message ||
        "Create failed";

      message.error(String(errMsg));
      return { ok: false, error: errMsg };
    },
    [userId, dispatch]
  );

  /* =========================
   * UPDATE
   * ========================= */
  const update = useCallback(
    async (id, patch) => {
      if (!userId || !id) {
        message.error("Invalid user or contact ID");
        return { ok: false };
      }

      const res = await dispatch(
        updateEmergencyContact({
          userId,
          id: String(id),
          patch,
        })
      );

      if (updateEmergencyContact.fulfilled.match(res)) {
        message.success("Contact updated");
        await dispatch(fetchEmergencyContacts({ userId }));
        return { ok: true, data: res.payload };
      }

      const errPayload = res?.payload;
      const errMsg =
        errPayload?.message ||
        (typeof errPayload === "string" ? errPayload : null) ||
        res?.error?.message ||
        "Update failed";

      message.error(String(errMsg));
      return { ok: false, error: errMsg };
    },
    [userId, dispatch]
  );

  /* =========================
   * DELETE
   * - version simple (direct)
   * - + version UI (Modal confirm)
   * ========================= */
  const remove = useCallback(async (id) => {
    if (!userId || !id) {
      message.error("User non authentifié.");
      return { ok: false };
    }
  
    const res = await dispatch(deleteEmergencyContact({ userId, id }));
  
    if (deleteEmergencyContact.fulfilled.match(res)) {
      message.success("Contact removed");
      await dispatch(fetchEmergencyContacts({ userId }));
      return { ok: true, data: res.payload };
    }
  
    const errMsg = res?.payload || res?.error?.message || "Delete failed";
    message.error(String(errMsg));
    return { ok: false, error: errMsg };
  }, [userId, dispatch]);
  


  // ✅ delete avec confirmation (comme code 1)
  const handleDelete = useCallback(
    (id) => {
      if (!userId) return message.error("User non authentifié.");

      Modal.confirm({
        title: "Delete contact?",
        content: "This action cannot be undone.",
        okText: "Delete",
        okType: "danger",
        onOk: async () => {
          await remove(id);
        },
      });
    },
    [userId, remove]
  );

  /* =========================
   * CALL
   * ========================= */
  const handleCall = useCallback((phone) => {
    const digits = cleanDigits(phone);
    if (!digits) return message.warning("Invalid phone");
    window.location.href = `tel:${digits}`;
  }, []);

  return {
    // data
    userId,
    contacts,
    status,
    error,
    isAddModalOpen,

    // actions
    handleRefresh,
    create,        // create(values)
    update,        // update(id, patch)
    remove,        // remove(id) direct
    handleDelete,  // delete with Modal confirm
    handleCall,
  };
}
